import { getYear, isWithinInterval } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useCallback, useMemo } from 'react';
import { OrderTicketCollection } from '../../model/orderTicket.types';

export type OrderTicketErrorContext = {
    field: 'firstname' | 'lastname' | 'birthday' | 'gender';
    errorMessage: string;
    messageContext?: Record<string, unknown>;
};

export type OrderTicketFieldErrorMap = Record<
    string,
    OrderTicketErrorContext[]
>;

export const useOrderTicketValidation = (
    orderTicketCollections: OrderTicketCollection[]
) => {
    const { t, i18n } = useTranslation();

    const validateOrder = useCallback((): OrderTicketFieldErrorMap => {
        const ticketHoldersErrorMap: OrderTicketFieldErrorMap = {};

        orderTicketCollections.forEach((ticketCollection) => {
            const ticketHolders = Object.values(
                ticketCollection.ticketHoldersMap
            ).flat();

            ticketHolders.forEach((ticketHolder) => {
                const holderErrors: OrderTicketErrorContext[] = [];
                if (!ticketHolder.gender || ticketHolder.gender === '--') {
                    holderErrors.push({
                        field: 'gender',
                        errorMessage: t(
                            'ticketOrder.validation.genderRequired'
                        ),
                    });
                }

                if (!ticketHolder.firstname) {
                    holderErrors.push({
                        field: 'firstname',
                        errorMessage: t(
                            'ticketOrder.validation.firstnameRequired'
                        ),
                    });
                }

                if (!ticketHolder.lastname) {
                    holderErrors.push({
                        field: 'lastname',
                        errorMessage: t(
                            'ticketOrder.validation.lastnameRequired'
                        ),
                    });
                }

                if (!ticketHolder.birthDay) {
                    holderErrors.push({
                        field: 'birthday',
                        errorMessage: t(
                            'ticketOrder.validation.birthdayRequired'
                        ),
                    });
                }

                if (ticketHolder.birthDay) {
                    const ticket =
                        ticketCollection.ticketMap[ticketHolder.ticketId];
                    const validityBirthdayRange = ticket.persons[0]?.birthday;

                    if (validityBirthdayRange) {
                        if (
                            !isWithinInterval(ticketHolder.birthDay, {
                                start: validityBirthdayRange.from,
                                end: validityBirthdayRange.to,
                            })
                        ) {
                            holderErrors.push({
                                field: 'birthday',
                                errorMessage: t(
                                    'ticketOrder.validation.birthdayRangeError',
                                    {
                                        fromYear: getYear(
                                            validityBirthdayRange.from
                                        ),
                                        toYear: getYear(
                                            validityBirthdayRange.to
                                        ),
                                    }
                                ),
                            });
                        }
                    }
                }

                if (holderErrors.length > 0) {
                    ticketHoldersErrorMap[ticketHolder.id] = holderErrors;
                }
            });
        });

        return ticketHoldersErrorMap;
    }, [orderTicketCollections, i18n.language, t]);

    const errorMap = useMemo(() => validateOrder(), [validateOrder]);
    const hasErrors = useMemo(
        () => Object.keys(errorMap).length > 0,
        [errorMap]
    );

    return {
        validationErrorMap: errorMap,
        hasErrors,
    };
};
